/**
 * @fileoverview Shared filter/form select atom.
 * @description A styled native `<select>` used for table filters and form fields across the app.
 * The native OS-drawn dropdown arrow is hidden and replaced with one consistent chevron icon,
 * matching LanguageSwitcher's approach, so the control looks the same across browsers/operating
 * systems. Visual variation (error state) is a closed prop, matching Input, rather than an
 * overridable className.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface SelectOption {
  value: string
  label: string
  disabled?: boolean | undefined
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[]
  invalid?: boolean
}

/**
 * @component
 * @param {SelectProps} props - Standard select attributes plus the options list and error state.
 * @param {SelectOption[]} props.options - The `{ value, label }` pairs rendered as `<option>`s.
 * @param {boolean} [props.invalid] - Shows the error border when true.
 */
function Select({ options, invalid = false, ...props }: SelectProps) {
  const borderClassName = invalid
    ? 'border-red-400 focus:ring-red-200'
    : 'border-input-border focus:ring-primary/20'

  return (
    <div className="relative">
      <select
        className={`h-[38px] w-full cursor-pointer appearance-none rounded-[8px] border bg-white pl-3 pr-8 text-sm font-medium text-ink focus:outline-none focus:ring-2 ${borderClassName}`}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate"
        aria-hidden="true"
      >
        <path d="M6 9.5l6 6 6-6" />
      </svg>
    </div>
  )
}

export default Select
